import { useQuery } from "@tanstack/react-query";

import {
  CapabilityPartParams,
  getCapabilityPart,
} from "../../services/productPlaneApi";
import {
  BearerGroup,
  CapabilityPartDetail,
} from "../../types/capabilityPartDetail";
import { queryKeys } from "./keys";
import { planeErrorMessage } from "./planeError";

// Sources asked for with a part: the most the backend gives in one page.
// The source table pages them itself, and a source mark finds its row there.
export const PART_SOURCES_LIMIT = 200;

// Bearers of one group asked for at a time
export const PART_BEARERS_LIMIT = 20;

// How many bearers of each group are skipped: where each tab's page starts
export type BearerOffsets = Record<BearerGroup, number>;

// Every group at its first page
export const FIRST_BEARER_PAGES: BearerOffsets = {
  products: 0,
  organizations: 0,
  projects: 0,
  categories: 0,
};

interface UseCapabilityPartResult {
  part: CapabilityPartDetail | null;
  // True while there is nothing to show yet
  loading: boolean;
  // True while `part` is the answer to the offsets asked for before, shown
  // until the answer to the ones asked for now arrives
  turning: boolean;
  // Why the part cannot be shown; null while it can, or while it loads
  error: string | null;
}

/**
 * TanStack Query hook for one capability part.
 *
 * The response holds one page of each bearer group. Another page of a group
 * is another request with that group's offset; while it loads, the previous
 * answer for the same part stays.
 *
 * @param partId - The id of the part; null fetches nothing
 * @param offsets - The bearers skipped, per group
 * @returns The part, the loading states and a message for a failed read
 */
export function useCapabilityPart(
  partId: string | null,
  offsets: BearerOffsets = FIRST_BEARER_PAGES,
): UseCapabilityPartResult {
  const params: CapabilityPartParams = {
    bearers_limit: PART_BEARERS_LIMIT,
    products_offset: offsets.products,
    organizations_offset: offsets.organizations,
    projects_offset: offsets.projects,
    categories_offset: offsets.categories,
    sources_limit: PART_SOURCES_LIMIT,
  };

  const { data, isLoading, isPlaceholderData, error } = useQuery({
    queryKey: queryKeys.productPlane.capabilityPart(partId!, { ...params }),
    queryFn: () => getCapabilityPart(partId!, params),
    enabled: !!partId,
    staleTime: 2 * 60 * 1000,
    // The previous answer stays for another page of the same part, never
    // for another part
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === partId ? previous : undefined,
  });

  return {
    part: data ?? null,
    loading: isLoading,
    turning: isPlaceholderData,
    error: error ? planeErrorMessage(error, "Capability part") : null,
  };
}
